import axios from 'axios';
import { api } from '@/api';

const types = {
  SET_BABYSITTERS: 'SET_BABYSITTERS',
  UPDATE_BABYSITTER: 'UPDATE_BABYSITTER',
  FETCH_FAILURE: 'FETCH_FAILURE',
  SET_LOADING_STATUS: 'SET_LOADING_STATUS',
  SET_BABYSITTER: 'SET_BABYSITTER',
  SET_AREA: 'SET_AREA',
  SET_EXPERIENCE: 'SET_EXPERIENCE',
  SET_LANGUAGES: 'SET_LANGUAGES',
  SET_ACTIVE_DRIVER: 'SET_ACTIVE_DRIVER',
  SET_FIRST_AID: 'SET_FIRST_AID',
  SET_COVID_VACCINATED: 'SET_COVID_VACCINATED',
  SET_VACATION_DATE: 'SET_VACATION_DATE',
  SET_HAS_UNPAID_BALANCE: 'SET_HAS_UNPAID_BALANCE',
  SET_WITH_DEACTIVATED: 'SET_WITH_DEACTIVATED',
  SET_CITY: 'SET_CITY',
  SET_POTENTIAL_BABYSITTER: 'SET_POTENTIAL_BABYSITTER',
  SET_AVAILABILITY: 'SET_AVAILABILITY',
  SET_AVAILABILITY_TYPE: 'SET_AVAILABILITY_TYPE',
  SET_MIN_SALARY: 'SET_MIN_SALARY',

  SET_CURRENT_PAGE: 'SET_CURRENT_PAGE',
  SET_ITEMS_PER_PAGE: 'SET_ITEMS_PER_PAGE',
  SET_TOTAL_ITEMS: 'SET_TOTAL_ITEMS',
};

export const state = {
  babysitters: [],
  loading: false,

  // filters
  babysitter: null,
  city: null,
  area: null,
  experience: [],
  languages: [],
  active_driver: null,
  first_aid: null,
  covid_vaccinated: null,
  vacation_date: null,
  has_unpaid_balance: null,
  with_deactivated: null,
  potential_babysitter: false,
  availability: [],
  availability_type: [],
  min_salary: null,
  currentPage: 1,
  itemsPerPage: 10,
  total: null,
};

export const mutations = {
  [types.SET_BABYSITTERS](state: any, data: any) {
    state.babysitters = [];

    let mappedData = data.data.map((sitter: any) => {
      sitter.experience = sitter.experience.map((ex: any) => ex.description);
      return sitter;
    });

    state.babysitters.push(...mappedData);
    state.total = data.meta.total ? data.meta.total : 1;
  },
  [types.SET_LOADING_STATUS](state: any, bool: boolean) {
    state.loading = bool;
  },

  [types.SET_BABYSITTER](state: any, data: string) {
    state.babysitter = data;
  },
  [types.SET_CITY](state: any, data: number) {
    state.city = data;
  },
  [types.SET_AREA](state: any, data: string) {
    state.area = data;
  },
  [types.SET_EXPERIENCE](state: any, data: number[]) {
    state.experience = data;
  },
  [types.SET_LANGUAGES](state: any, data: number[]) {
    state.languages = data;
  },
  [types.SET_ACTIVE_DRIVER](state: any, data: number) {
    state.active_driver = data ? true : null;
  },
  [types.SET_FIRST_AID](state: any, data: number) {
    state.first_aid = data ? true : null;
  },
  [types.SET_COVID_VACCINATED](state: any, data: number) {
    state.covid_vaccinated = data;
  },
  [types.SET_VACATION_DATE](state: any, data: number) {
    state.vacation_date = data;
  },
  [types.SET_HAS_UNPAID_BALANCE](state: any, data: number) {
    state.has_unpaid_balance = data;
  },
  [types.SET_POTENTIAL_BABYSITTER](state: any, data: number) {
    state.potential_babysitter = data;
  },
  [types.SET_WITH_DEACTIVATED](state: any, data: number) {
    state.with_deactivated = data;
  },

  [types.SET_AVAILABILITY](state: any, data: string[]) {
    state.availability = data;
  },

  [types.SET_AVAILABILITY_TYPE](state: any, data: string[]) {
    state.availability_type = data;
  },

  [types.SET_MIN_SALARY](state: any, data: number | null) {
    state.min_salary = data;
  },

  [types.SET_CURRENT_PAGE](state: any, data: number) {
    state.currentPage = data;
  },

  [types.SET_ITEMS_PER_PAGE](state: any, data: number) {
    state.itemsPerPage = data;
  },
};

export const actions = {
  async fetchBabysitters({ commit, getters }: any, params: any) {
    try {
      commit(types.SET_LOADING_STATUS, true);
      let params = getters.payload;

      if (!params.potential_babysitter) {
        params.potential_babysitter = false;
      }
      params.sort_activated = true;

      const { data } = await axios.get(api.path('v1.admin.babysitters'), {
        params: params,
      });

      commit(types.SET_BABYSITTERS, data);
      commit(types.SET_LOADING_STATUS, false);
    } catch (e) {
      commit(types.FETCH_FAILURE);
      commit(types.SET_LOADING_STATUS, false);
    }
  },

  clearPayload({ commit }: any, payload: any) {
    commit(types.SET_BABYSITTER, null);
    commit(types.SET_CITY, null);
    commit(types.SET_AREA, null);
    commit(types.SET_EXPERIENCE, []);
    commit(types.SET_LANGUAGES, []);
    commit(types.SET_COVID_VACCINATED, null);
    commit(types.SET_VACATION_DATE, null);
    commit(types.SET_HAS_UNPAID_BALANCE, null);
    commit(types.SET_POTENTIAL_BABYSITTER, null);
    commit(types.SET_WITH_DEACTIVATED, null);
    commit(types.SET_AVAILABILITY, []);
    commit(types.SET_AVAILABILITY_TYPE, []);
    commit(types.SET_CURRENT_PAGE, 1);
    commit(types.SET_ITEMS_PER_PAGE, 10);
    commit(types.SET_ACTIVE_DRIVER, null);
  },

  setBabysitters({ commit }: any, payload: any) {
    commit(types.SET_BABYSITTERS, payload);
  },
  setBabysittersLoading({ commit }: any, payload: any) {
    commit(types.SET_LOADING_STATUS, payload);
  },

  setBabysitter({ commit }: any, payload: any) {
    commit(types.SET_BABYSITTER, payload);
  },
  setCity({ commit }: any, payload: any) {
    commit(types.SET_CITY, payload);
  },
  setArea({ commit }: any, payload: any) {
    commit(types.SET_AREA, payload);
  },
  setExperience({ commit }: any, payload: any) {
    commit(types.SET_EXPERIENCE, payload);
  },
  setLanguages({ commit }: any, payload: any) {
    commit(types.SET_LANGUAGES, payload);
  },
  setActiveDriver({ commit }: any, payload: any) {
    commit(types.SET_ACTIVE_DRIVER, payload);
  },
  setFirstAid({ commit }: any, payload: any) {
    commit(types.SET_FIRST_AID, payload);
  },
  setCovidVaccinated({ commit }: any, payload: any) {
    commit(types.SET_COVID_VACCINATED, payload);
  },
  setVacationDate({ commit }: any, payload: any) {
    commit(types.SET_VACATION_DATE, payload);
  },
  setHasUnpaidBalance({ commit }: any, payload: any) {
    commit(types.SET_HAS_UNPAID_BALANCE, payload);
  },
  setIsPotentialBabysitter({ commit }: any, payload: any) {
    commit(types.SET_POTENTIAL_BABYSITTER, payload);
  },
  setWithDeactivated({ commit }: any, payload: any) {
    commit(types.SET_WITH_DEACTIVATED, payload);
  },

  setAvailability({ commit }: any, payload: string[]) {
    commit(types.SET_AVAILABILITY, payload);
  },

  setAvailabilityType({ commit }: any, payload: string[]) {
    commit(types.SET_AVAILABILITY_TYPE, payload);
  },

  setMinSalary({ commit }: any, payload: number | null) {
    commit(types.SET_MIN_SALARY, payload);
  },

  setCurrentPage({ commit }: any, payload: any) {
    commit(types.SET_CURRENT_PAGE, payload);
  },

  setItemsPerPage({ commit }: any, payload: any) {
    commit(types.SET_ITEMS_PER_PAGE, payload);
  },
};

export const getters = {
  babysitters: (state: any) => state.babysitters,
  babysittersLoading: (state: any) => state.loading,
  total: (state: any) => state.total,

  babysitter: (state: any) => state.babysitter,
  city: (state: any) => state.city,
  area: (state: any) => state.area,
  experience: (state: any) => state.experience,
  languages: (state: any) => state.languages,
  activeDriver: (state: any) => state.active_driver,
  firstAid: (state: any) => state.first_aid,
  covidVaccinated: (state: any) => state.covid_vaccinated,
  vacationDate: (state: any) => state.vacation_date,
  hasUnpaidBalance: (state: any) => state.has_unpaid_balance,
  isPotentialBabysitter: (state: any) => state.potential_babysitter,
  withDeactivated: (state: any) => state.with_deactivated,
  availability: (state: any) => state.availability,
  availabilityType: (state: any) => state.availability_type,
  minSalary: (state: any) => state.min_salary,

  currentPage: (state: any) => state.currentPage,
  itemsPerPage: (state: any) => state.itemsPerPage,

  payload: (state: any) => {
    console.log('🚀 ~ state:', state);

    return {
      babysitter: state.babysitter,
      city: state.city,
      area: state.area,
      experience: state.experience,
      languages: state.languages,
      active_driver: state.active_driver,
      first_aid: state.first_aid,
      vacation_date: state.vacation_date,
      has_unpaid_balance: state.has_unpaid_balance
        ? state.has_unpaid_balance
        : null,
      with_deactivated: state.with_deactivated ? state.with_deactivated : null,
      availability_times: state.availability,
      available_full_time: state.availability_type.includes('full_time'),
      available_part_time: state.availability_type.includes('part_time'),
      salary_range_min: state.min_salary,
      page: state.currentPage,
      per_page: state.itemsPerPage,
      potential_babysitter: state.potential_babysitter,
    };
  },
};
